import { useQuery } from "@tanstack/react-query";
import { useSettings } from "../hooks/usePosts";
import { api } from "../api/client";
import { ApiKeyRow } from "../components/ApiKeyRow";

export function Settings() {
  const { data, isLoading, error, refetch, isFetching } = useSettings();
  const keys = useQuery({
    queryKey: ["api_keys"],
    queryFn: api.settings.getKeys,
  });

  if (isLoading) return <div className="text-brand-text-dark/60">Lade…</div>;
  if (error || !data)
    return (
      <div className="rounded-xl bg-red-100 p-6 text-red-900">
        Fehler: {String(error)}
      </div>
    );

  return (
    <div className="space-y-8">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="headline text-6xl text-brand-text-dark">Einstellungen</h1>
          <p className="mt-2 text-sm text-brand-text-dark/60">
            Service-Health, aktive Modelle und API-Keys. Alle Keys werden in{" "}
            <code className="rounded bg-black/5 px-1.5">
              {keys.data?.env_path ?? "config/secrets.env"}
            </code>{" "}
            gespeichert.
          </p>
        </div>
        <button
          onClick={() => {
            void refetch();
            void keys.refetch();
          }}
          disabled={isFetching}
          className="rounded-full bg-brand-text-dark px-4 py-2 text-xs font-bold uppercase tracking-wider text-brand-cream hover:bg-brand-text-dark/85 disabled:opacity-50"
        >
          {isFetching ? "Checking…" : "Refresh"}
        </button>
      </div>

      <Section title="Service health">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {data.services.map((s) => (
            <div
              key={s.name}
              className="flex items-start gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5"
            >
              <div
                className={`mt-1 h-3 w-3 flex-shrink-0 rounded-full ${
                  s.available ? "bg-emerald-500" : "bg-red-400"
                }`}
              />
              <div className="flex-1">
                <div className="font-bold text-brand-text-dark">{s.name}</div>
                <div className="mt-1 text-xs text-brand-text-dark/60">
                  {s.available
                    ? "Reachable / configured"
                    : (s.detail ?? "Not configured")}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="API keys"
        subtitle="Alle Keys die die Pipeline braucht. Trag fehlende nach oder aktualisiere sie hier — der Server liest sie sofort neu ein."
      >
        {keys.isLoading ? (
          <div className="text-brand-text-dark/60">Lade Keys…</div>
        ) : keys.error ? (
          <div className="rounded-xl bg-red-100 p-4 text-sm text-red-900">
            {String(keys.error)}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {keys.data?.keys.map((k) => <ApiKeyRow key={k.name} entry={k} />)}
          </div>
        )}
      </Section>

      <Section title="Active models">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
            <ModelRow label="ElevenLabs voice" value={data.tts_voice_id ?? "—"} />
            <ModelRow label="ElevenLabs model" value={data.tts_model_id ?? "—"} />
            <ModelRow label="WaveSpeed video" value={data.wavespeed_video_model ?? "—"} />
          </dl>
          <p className="mt-4 text-xs text-brand-text-dark/50">
            Die Felder sind auch oben in „API keys" editierbar.
          </p>
        </div>
      </Section>

      <Section title="Notion sync">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <p className="text-sm text-brand-text-dark">
            Status: {data.notion_sync_enabled ? "✅ ON" : "⏸ OFF"}
          </p>
          <p className="mt-2 text-xs text-brand-text-dark/60">
            Toggle via env var{" "}
            <code className="rounded bg-black/5 px-1.5">NOTION_SYNC=on|off</code>.
            When ON, Approvals in this UI mirror to your Notion content
            calendar. When OFF, everything stays local.
          </p>
        </div>
      </Section>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-brand-text-dark/50">
        {title}
      </h2>
      {subtitle ? (
        <p className="mb-4 text-xs text-brand-text-dark/55">{subtitle}</p>
      ) : (
        <div className="mb-4" />
      )}
      {children}
    </section>
  );
}

function ModelRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-brand-text-dark/50">
        {label}
      </div>
      <div className="mt-1 font-mono text-xs text-brand-text-dark">{value}</div>
    </div>
  );
}
